import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import { ChevronDown, RotateCcw } from "lucide-react";

function CountrySelect({ countries, value, onChange, testid, placeholder }) {
  const { lang } = useLang();
  return (
    <div className="relative">
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full h-12 appearance-none rounded-xl border border-stoneLine bg-card px-4 pe-10 text-sm text-ink outline-none focus:border-ochre transition-colors"
        data-testid={testid}
      >
        <option value="">{placeholder}</option>
        {countries.map((c) => (
          <option key={c.code} value={c.code}>{c.short_names[lang]}</option>
        ))}
      </select>
      <ChevronDown className="absolute top-1/2 -translate-y-1/2 end-3 w-4 h-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}

function CompareCard({ country, side }) {
  const { lang, t } = useLang();
  if (!country) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-stoneLine bg-card/40 p-8 min-h-[420px] flex items-center justify-center text-muted-foreground text-sm">
        {t(side === "a" ? "compare_country_a" : "compare_country_b")}
      </div>
    );
  }
  return (
    <div className="rounded-3xl border border-stoneLine bg-card p-6" data-testid={`compare-card-${side}`}>
      <img src={country.flag_url} alt="" className="w-24 h-16 object-cover rounded-md border border-stoneLine mb-4" />
      <h3 className="text-2xl font-display font-bold text-ink leading-tight">{country.names[lang]}</h3>
      <p className="text-sm text-muted-foreground mb-6">{country.capital[lang]}</p>

      <div className="space-y-4">
        <Row label={t("area")} value={`${fmtNumber(country.area_km2, lang)} ${t("km2")}`} />
        <Row label={t("population")} value={fmtNumber(country.population, lang)} />
        <Row label={t("currency")} value={country.currency[lang]} small />
        <Row label={t("gov_type")} value={country.gov_type[lang]} small />
        <Row label={t("economy")} value={country.economy_base[lang]} small multiline />
      </div>
    </div>
  );
}

function Row({ label, value, small, multiline }) {
  return (
    <div className="border-t border-stoneLine/60 pt-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</div>
      <div className={`${small ? "text-sm" : "stat-number text-xl"} text-ink ${multiline ? "leading-relaxed" : ""}`}>{value}</div>
    </div>
  );
}

export default function Compare() {
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  const countryA = countries.find((c) => c.code === a) || null;
  const countryB = countries.find((c) => c.code === b) || null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <header className="text-center mb-10">
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-ink tracking-tight">{t("compare")}</h1>
        <p className="mt-2 text-muted-foreground">{t("compare_pick")}</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <CountrySelect countries={countries} value={a} onChange={setA} placeholder={t("compare_country_a")} testid="compare-select-a" />
        <CountrySelect countries={countries} value={b} onChange={setB} placeholder={t("compare_country_b")} testid="compare-select-b" />
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={() => { setA(null); setB(null); }}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-stoneLine hover:bg-sandDeep/50 transition-colors"
          data-testid="compare-reset"
        >
          <RotateCcw className="w-4 h-4" /> {t("reset")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CompareCard country={countryA} side="a" />
        <CompareCard country={countryB} side="b" />
      </div>
    </div>
  );
}
